/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://statefulcoders.com/product/material-dashboard-react
* Copyright 2025 Stateful Coders (https://https://statefulcoders.com)

Coded by https://statefulcoders.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Drawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";

export default styled(Drawer)(({ theme, ownerState }) => {
  const { palette, boxShadows, transitions, breakpoints, functions } = theme;
  const { transparentSidenav, whiteSidenav, miniSidenav, darkMode } = ownerState;

  const sidebarWidth = 250;
  const miniSidebarWidth = 96;
  const { transparent, gradients, white, background } = palette;
  const { xxl } = boxShadows;
  const { linearGradient } = functions;

  let backgroundValue = darkMode
    ? background.sidenav
    : linearGradient(gradients.dark.main, gradients.dark.state);

  if (transparentSidenav) {
    backgroundValue = transparent.main;
  } else if (whiteSidenav) {
    backgroundValue = white.main;
  }

  // Mobile styles - always full width drawer that slides in/out
  const mobileStyles = () => ({
    background: backgroundValue,
    width: sidebarWidth,
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1300,
    transform: miniSidenav ? `translateX(-${sidebarWidth}px)` : "translateX(0)",
    transition: transitions.create("transform", {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),
    boxShadow: xxl,
  });

  // Desktop styles for the sidenav when miniSidenav={false}
  const drawerOpenStyles = () => ({
    background: backgroundValue,
    width: sidebarWidth,
    transform: "translateX(0)",
    transition: transitions.create(["width", "transform"], {
      easing: transitions.easing.sharp,
      duration: transitions.duration.enteringScreen,
    }),
    boxShadow: transparentSidenav ? "none" : xxl,
    marginBottom: transparentSidenav ? 0 : "inherit",
    left: "0",
  });

  // Desktop styles for the sidenav when miniSidenav={true}
  const drawerCloseStyles = () => ({
    background: backgroundValue,
    width: miniSidebarWidth,
    overflowX: "hidden",
    transform: "translateX(0)",
    transition: transitions.create(["width", "transform"], {
      easing: transitions.easing.sharp,
      duration: transitions.duration.shorter,
    }),
    boxShadow: transparentSidenav ? "none" : xxl,
    marginBottom: transparentSidenav ? 0 : "inherit",
    left: "0",
  });

  return {
    "& .MuiDrawer-paper": {
      border: "none",

      // Mobile-first approach - restore original mobile behavior
      [breakpoints.down("xl")]: {
        ...mobileStyles(),
      },

      // Desktop styles - always keep sidenav open (fixed)
      [breakpoints.up("xl")]: {
        ...drawerOpenStyles(),
      },
    },
  };
});
